import { Flex, Text } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlatformIcon } from '@/components/creator/PlatformIcon';
import { platformAccentColor } from '@/components/creator/platform-accents';
import { mediaGlassPillProps } from './mentionCardStyle';
import { mentionOriginLabelKey } from './mentionOriginLabelKey';

interface MentionCardPlatformChipProps {
  mention: Mention;
  /** Icon-only disc for the dense variant (frame + type label are dropped). */
  compact?: boolean;
}

/**
 * Chip pinned to the media's top-left. Full variant is a glass pill with the
 * platform disc plus the origin label; compact drops the frame and label to a
 * bare disc — enlarged (22 vs 18), since it has no pill around it to lend it
 * presence. Purely decorative — the parent card's aria-label is the accessible
 * target.
 */
const MentionCardPlatformChip: React.FC<MentionCardPlatformChipProps> = ({
  mention,
  compact = false,
}) => {
  const { t } = useTranslation();
  const accent = platformAccentColor(mention.platform);
  const labelKey = mentionOriginLabelKey(mention);

  return (
    <Flex
      position="absolute"
      top={compact ? 2 : 2.5}
      left={compact ? 2 : 2.5}
      align="center"
      gap={1.5}
      aria-hidden
      {...(compact ? {} : { h: '26px', pl: 1, pr: 2.5, ...mediaGlassPillProps })}
    >
      <Flex
        align="center"
        justify="center"
        boxSize={compact ? '22px' : '18px'}
        rounded="full"
        bg={accent}
        color="white"
        flexShrink={0}
      >
        <PlatformIcon platform={mention.platform} size={compact ? 11 : 10} />
      </Flex>
      {!compact && labelKey && (
        <Text
          fontFamily="heading"
          fontSize="10px"
          fontWeight="semibold"
          letterSpacing="0.07em"
          textTransform="uppercase"
          color="primary.600"
          lineHeight="1"
        >
          {t(labelKey)}
        </Text>
      )}
    </Flex>
  );
};

export default MentionCardPlatformChip;
